import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Stack, CircularProgress } from "@mui/material";
import TopBar from "../../../../../component/TopBar";
import MainBox from "../../../container/MainBox";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import EmptyState from "../../../../../component/EmptyState";
import DataTable from "../../../../../component/DataTable";
import { StatusChip, personName } from "../../../../../component/listing";
import { getReviewCycle, getReviewsByCycle, activateReviewCycle, closeReviewCycle, reopenReviewCycle } from "../../../../../services/performance";
import { useNotify } from "../../../../../component/NotificationProvider";
import CycleFormDialog from "./CycleFormDialog";
import { reviewStatusColumn, reviewRatingColumn } from "./reviewColumns";
import ReopenDialog from "../../../../../component/performance/ReopenDialog";
import { tokens, displayFont } from "../../../../../theme";

const TABLE_SX = { width: "100%", tableLayout: "fixed" };

// The scale belongs to this one cycle, so every row reads it from the page
// rather than from its own populated copy.
const columnsFor = (cycle) => [
  {
    key: "employee", label: "Employee", cellSx: { fontWeight: 600 },
    render: (r) => (
      <>
        {personName(r.employee)}
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, fontWeight: 500 }}>{r.employee?.designation || ""}</Box>
      </>
    ),
  },
  { key: "reviewer", label: "Reviewer", render: (r) => personName(r.reviewer) },
  reviewStatusColumn,
  reviewRatingColumn(() => cycle?.ratingScale?.max || 5),
];

const CycleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const [cycle, setCycle] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([getReviewCycle(id), getReviewsByCycle(id)]);
      setCycle(c?.data || null);
      setReviews(Array.isArray(r?.data) ? r.data : []);
    } catch (e) { notify.error(e?.message || "Failed to load cycle"); }
    finally { setLoading(false); }
  }, [id, notify]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { const res = await fn(id); if (res?.success === false) throw new Error(res?.message); notify.success(msg); fetchData(); }
    catch (e) { notify.error(e?.message || "Action failed"); } finally { setBusy(false); }
  };

  if (loading) return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Performance"} />
      <MainBox sx={{ padding: "1.25rem" }}>
        <DataTable columns={columnsFor(null)} loading tableSx={TABLE_SX} />
      </MainBox>
    </Box>
  );
  if (!cycle) return <Box sx={{ width: "100%" }}><TopBar MainHeading={"Performance"} /><EmptyState title="Cycle not found" /></Box>;

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Performance"} breadcrumbs={[{ label: "Performance", href: "/app/performance" }, { label: cycle.name }]} />
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: "0.5rem" }}>
              <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>{cycle.name}</Box>
              <StatusChip label={cycle.status} tone={cycle.status === "active" ? "info" : cycle.status === "closed" ? "success" : "muted"} />
            </Stack>
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
              Competencies: {(cycle.competencies || []).map((c) => `${c.name} (×${c.weight})`).join(", ") || "—"} · Scale 1–{cycle.ratingScale?.max || 5}
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            {/* a draft is still editable, and activation refuses without at
                least one competency — so the way to fix that has to be here,
                not only in the create dialog */}
            {cycle.status === "draft" && <BtnSecondary onClick={() => setEditOpen(true)} sx={{ borderRadius: "8px" }}>Edit</BtnSecondary>}
            {cycle.status === "draft" && <BtnPrimary onClick={() => act(activateReviewCycle, "Cycle activated — reviews generated")} disabled={busy} sx={{ borderRadius: "8px" }}>{busy ? <CircularProgress size={"1.1rem"} /> : "Activate"}</BtnPrimary>}
            {cycle.status === "closed" && <BtnSecondary onClick={() => setReopenOpen(true)} sx={{ borderRadius: "8px" }}>Reopen cycle</BtnSecondary>}
            {cycle.status === "active" && <BtnSecondary onClick={() => act(closeReviewCycle, "Cycle closed")} disabled={busy} sx={{ borderRadius: "8px" }}>Close cycle</BtnSecondary>}
          </Stack>
        </Stack>

        <DataTable
          columns={columnsFor(cycle)}
          rows={reviews}
          tableSx={TABLE_SX}
          empty={{
            title: cycle.status === "draft" ? "Activate to generate reviews" : "No participants",
            description: cycle.status === "draft" ? "Reviews are created for each participant when you activate the cycle." : "",
          }}
          onRowClick={(r) => navigate(`/app/performance/review/${r._id}`)}
        />
      </MainBox>
      <CycleFormDialog open={editOpen} cycle={cycle} onClose={() => setEditOpen(false)} onDone={fetchData} />
      <ReopenDialog
        open={reopenOpen}
        title={`Reopen ${cycle.name}?`}
        consequence="The cycle goes back to active and goals in it can be updated again. Reviews already finalized stay that way — reopen those individually if a rating needs to change."
        confirmLabel="Reopen cycle"
        onClose={() => setReopenOpen(false)}
        onConfirm={async (reason) => {
          const res = await reopenReviewCycle(cycle._id, reason);
          if (res?.success !== false) { notify.success("Cycle reopened"); fetchData(); }
          return res;
        }}
      />
    </Box>
  );
};

export default CycleDetail;
